/**
 * TanStack Query hooks for `campaign-grouping`. Thin wrappers over ./api.
 * All mutations invalidate the `campaign-grouping` key family so the groups
 * list + per-group campaign list stay in sync.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	assignCampaigns,
	campaignGroupingQueryKeys,
	createGroup,
	deleteGroup,
	listCampaignsInGroup,
	listGroups,
	removeCampaign,
	renameGroup,
	type AssignCampaignResponse,
	type CreateGroupBody,
	type GroupCampaignsResponse,
	type GroupResponse,
	type ListGroupsResponse,
	type PatchGroupBody,
} from "./api";

export function useCampaignGroupingList(): UseQueryResult<ListGroupsResponse, Error> {
	return useQuery({
		queryKey: campaignGroupingQueryKeys.list(),
		queryFn: listGroups,
	});
}

export function useCampaignGroupingCampaigns(
	groupId: string | null | undefined,
): UseQueryResult<GroupCampaignsResponse, Error> {
	return useQuery({
		queryKey: campaignGroupingQueryKeys.campaigns(groupId ?? "__none__"),
		queryFn: () => listCampaignsInGroup(groupId as string),
		enabled: !!groupId,
	});
}

export function useCampaignGroupingCreate(): UseMutationResult<
	GroupResponse,
	Error,
	CreateGroupBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createGroup,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: campaignGroupingQueryKeys.all });
		},
	});
}

export function useCampaignGroupingRename(): UseMutationResult<
	GroupResponse,
	Error,
	{ id: string; body: PatchGroupBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }) => renameGroup(id, body),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: campaignGroupingQueryKeys.all });
		},
	});
}

export function useCampaignGroupingDelete(): UseMutationResult<
	{ deleted: boolean; id: string },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: deleteGroup,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: campaignGroupingQueryKeys.all });
		},
	});
}

export function useCampaignGroupingAssign(): UseMutationResult<
	AssignCampaignResponse[],
	Error,
	{ groupId: string; campaignIds: string[] }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ groupId, campaignIds }) =>
			assignCampaigns(groupId, campaignIds),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: campaignGroupingQueryKeys.all });
		},
	});
}

export function useCampaignGroupingRemoveCampaign(): UseMutationResult<
	{ deleted: boolean; group_id: string; campaign_id: string },
	Error,
	{ groupId: string; campaignId: string }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ groupId, campaignId }) => removeCampaign(groupId, campaignId),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: campaignGroupingQueryKeys.all });
		},
	});
}
